/**
 * Lead Generation API
 * API functions for AI-powered lead discovery and web scraping
 * Phase 5 - Frontend Integration
 */

import { apiClient } from './aiCrmApi';
import type { ApiResponse } from './aiCrmApi';
import type { CampaignHintsResponse } from './emailCampaignApi';
import type { LeadSearchResult } from '../components/LeadSearchResultCard';
import type {
  GeneratedLead,
  GeneratedLeadSearchResult,
  GeneratedLeadSearchRequest,
} from '../types';

// ===== TYPES =====

export interface ScrapingFilters {
  keywords?: string[];
  industries?: string[];
  location?: string;
  employeeSize?: string;
  tags?: string[];
  prompt?: string; // Free-text prompt for AI processing
}

export interface AISelectedSource {
  source: string;
  reason: string;
  expectedResults: number;
}

/**
 * Chat message for multi-turn conversations with the AI
 */
export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
}

export interface StartScrapingJobInput {
  source: 'linkedin' | 'business-directory' | 'industry-website' | 'custom-url';
  filters?: ScrapingFilters;
  customUrl?: string;
  chatHistory?: ChatMessage[]; // For multi-turn conversations when answering clarification questions
}

export interface ScrapingJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'needs_clarification';
  source: string;
  filters: ScrapingFilters;
  customUrl?: string | null;
  progress: number;
  totalFound: number;
  createdAt: string;
  completedAt?: string;
  estimatedCompletion?: string;
  error?: string | null;
  sourcesSelected?: AISelectedSource[];
  questions?: string[]; // Clarifying questions from AI when status is 'needs_clarification'
}

export interface JobResults {
  jobId: string;
  results: LeadSearchResult[];
  total: number;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'needs_clarification';
  questions?: string[];
}

export interface JobResultsQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface AddResultToLeadsInput {
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  industry?: string;
  employeeCount?: string;
  website?: string;
  location?: string;
  description?: string;
  dataSource: string;
  potentialRating: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';
  aiScore?: number;
  potentialValue?: number;
  aiRecommendation?: string;
  tags?: string[];
}

export interface BulkAddResultsInput {
  results: AddResultToLeadsInput[];
}

export interface BulkAddResultsResponse {
  created: number;
  failed: number;
  leads: any[];
  errors?: Array<{
    email: string;
    error: string;
  }>;
}

// ===== FAST DISCOVERY TYPES =====

export interface FastDiscoveryInput {
  prompt: string;
  userBackground?: string;
  chatHistory?: ChatMessage[];
}

export interface FastDiscoveryResponse {
  jobId: string;
  status: string;
  estimatedCompletion: string;
  estimatedResults: number;
  sourcesSelected: AISelectedSource[];
  message: string;
  flowType: string;
}

// ===== API FUNCTIONS =====

export const leadGenerationApi = {
  /**
   * Fast Discovery - New simplified prompt-based lead discovery
   * Uses Gemini + Google Search grounding for quick results (~15 seconds)
   * Only accepts: prompt, userBackground, chatHistory
   */
  startFastDiscovery: async (data: FastDiscoveryInput): Promise<FastDiscoveryResponse> => {
    const response = await apiClient.post<ApiResponse<FastDiscoveryResponse>>(
      '/lead-generation/fast-discovery',
      data
    );
    return response.data.data;
  },

  /**
   * Start a new web scraping job (Legacy)
   */
  startScraping: async (data: StartScrapingJobInput): Promise<ScrapingJob> => {
    const response = await apiClient.post<ApiResponse<ScrapingJob>>(
      '/lead-generation/scrape',
      data
    );
    return response.data.data;
  },

  /**
   * Get scraping job status
   */
  getJobStatus: async (jobId: string): Promise<ScrapingJob> => {
    const response = await apiClient.get<ApiResponse<ScrapingJob>>(
      `/lead-generation/jobs/${jobId}`
    );
    return response.data.data;
  },

  /**
   * Get scraping job results
   */
  getJobResults: async (jobId: string, params?: JobResultsQuery): Promise<JobResults> => {
    const response = await apiClient.get<ApiResponse<JobResults>>(
      `/lead-generation/results/${jobId}`,
      { params }
    );
    return response.data.data;
  },

  /**
   * Add a single scraped result to CRM as a lead
   */
  addResultToLeads: async (resultId: string, data: AddResultToLeadsInput): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>(
      `/lead-generation/results/${resultId}/add`,
      data
    );
    return response.data.data;
  },

  /**
   * Bulk add multiple scraped results to CRM
   */
  bulkAddResults: async (data: BulkAddResultsInput): Promise<BulkAddResultsResponse> => {
    const response = await apiClient.post<ApiResponse<BulkAddResultsResponse>>(
      '/lead-generation/results/bulk-add',
      data
    );
    return response.data.data;
  },

  // ===== GENERATED LEADS (Global Pool) =====

  /**
   * Search generated leads - combines database + AI discovery
   */
  searchGeneratedLeads: async (request: GeneratedLeadSearchRequest): Promise<GeneratedLeadSearchResult> => {
    const response = await apiClient.post<ApiResponse<GeneratedLeadSearchResult>>(
      '/lead-generation/search',
      request
    );
    return response.data.data;
  },

  /**
   * Get proven leads (verified or converted)
   */
  getProvenLeads: async (limit: number = 20): Promise<{ leads: GeneratedLead[]; total: number }> => {
    const response = await apiClient.get<ApiResponse<{ leads: GeneratedLead[]; total: number }>>(
      `/lead-generation/proven?limit=${limit}`
    );
    return response.data.data;
  },

  /**
   * Reject a generated lead
   */
  rejectGeneratedLead: async (generatedLeadId: string): Promise<GeneratedLead> => {
    const response = await apiClient.post<ApiResponse<GeneratedLead>>(
      `/lead-generation/generated/${generatedLeadId}/reject`
    );
    return response.data.data;
  },

  /**
   * Generate AI recommendation for a lead
   */
  generateRecommendation: async (generatedLeadId: string): Promise<{ recommendation: string; lead: GeneratedLead }> => {
    const response = await apiClient.post<ApiResponse<{ recommendation: string; lead: GeneratedLead }>>(
      `/lead-generation/generated/${generatedLeadId}/recommendation`
    );
    return response.data.data;
  },

  /**
   * Enrich a generated lead (Stage 2 - Deep Enrichment)
   */
  enrichGeneratedLead: async (generatedLeadId: string): Promise<GeneratedLead> => {
    const response = await apiClient.post<ApiResponse<GeneratedLead>>(
      `/lead-generation/generated/${generatedLeadId}/enrich`
    );
    return response.data.data;
  },

  /**
   * Find more contacts for a generated lead (async — starts background pipeline)
   */
  findMoreContacts: async (generatedLeadId: string): Promise<{ jobId: string; status: string }> => {
    const response = await apiClient.post<ApiResponse<{ jobId: string; status: string }>>(
      `/lead-generation/generated/${generatedLeadId}/find-more-contacts`
    );
    return response.data.data;
  },

  /**
   * Get a single generated lead by ID — includes revealedContactIndices for the current company
   */
  getGeneratedLeadById: async (generatedLeadId: string): Promise<GeneratedLead & { revealedContactIndices: number[] }> => {
    const response = await apiClient.get<ApiResponse<GeneratedLead & { revealedContactIndices: number[] }>>(
      `/lead-generation/generated/${generatedLeadId}`
    );
    return response.data.data;
  },

  /**
   * Record that contacts have been revealed — persists across modal opens, triggers credit charge
   */
  revealContacts: async (generatedLeadId: string, contactIndices: number[]): Promise<{ revealedCount: number }> => {
    const response = await apiClient.post<ApiResponse<{ revealedCount: number }>>(
      `/lead-generation/generated/${generatedLeadId}/reveal`,
      { contactIndices }
    );
    return response.data.data;
  },

  /**
   * Add a generated lead to CRM
   */
  addGeneratedLeadToCRM: async (
    generatedLeadId: string,
    additionalData?: { contactName?: string; email?: string; phone?: string; customAttributes?: Record<string, string> }
  ): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>(
      `/lead-generation/generated/${generatedLeadId}/add-to-crm`,
      additionalData || {}
    );
    return response.data.data;
  },

  /**
   * Verify contact information for a generated lead
   */
  verifyContact: async (generatedLeadId: string): Promise<GeneratedLead> => {
    const response = await apiClient.post<ApiResponse<GeneratedLead>>(
      `/lead-generation/generated/${generatedLeadId}/verify-contact`
    );
    return response.data.data;
  },

  /**
   * Store AI discovery results in database
   */
  storeAIResults: async (results: any[], searchPrompt?: string): Promise<{ stored: number; leads: GeneratedLead[] }> => {
    const response = await apiClient.post<ApiResponse<{ stored: number; leads: GeneratedLead[] }>>(
      '/lead-generation/store-results',
      { results, searchPrompt }
    );
    return response.data.data;
  },

  // ===== SCRAPING RESULT ENRICHMENT =====

  /**
   * Enrich a scraping job result with detailed AI analysis
   * Uses the Python AI backend's shortlist-lead endpoint
   */
  enrichScrapingResult: async (jobId: string, resultId: string): Promise<{
    enrichmentJobId: string;
    status: 'processing' | 'completed' | 'already_enriched';
    result?: any;
    message: string;
  }> => {
    const response = await apiClient.post<ApiResponse<any>>(
      `/lead-generation/results/${jobId}/${resultId}/enrich`
    );
    return response.data.data;
  },

  /**
   * Get the status of an enrichment job
   */
  getEnrichmentStatus: async (enrichmentJobId: string): Promise<{
    enrichmentJobId: string;
    status: 'processing' | 'completed' | 'failed';
    progress?: number;
    result?: any;
    error?: string;
  }> => {
    const response = await apiClient.get<ApiResponse<any>>(
      `/lead-generation/enrichment-status/${enrichmentJobId}`
    );
    return response.data.data;
  },

  /**
   * Get lead generation hints (reuses campaign hints endpoint which includes lead_generation advice)
   */
  getLeadGenerationHints: async (): Promise<CampaignHintsResponse> => {
    const response = await apiClient.get<ApiResponse<CampaignHintsResponse>>(
      '/campaigns/hints'
    );
    return response.data.data;
  },

  // ===== ASYNC BATCH ENRICHMENT PIPELINE =====

  /**
   * Trigger batch enrichment for multiple leads (fire-and-forget on backend)
   */
  startBatchEnrichment: async (leadIds: string[]): Promise<{
    totalLeads: number;
    toEnrich: number;
    alreadyEnriched: number;
    status: 'RUNNING' | 'ALREADY_DONE';
  }> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/lead-generation/batch-enrich',
      { leadIds }
    );
    return response.data.data;
  },

  /**
   * Poll enrichment progress for a set of leads
   */
  getEnrichmentProgress: async (leadIds: string[]): Promise<{
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
    pending: number;
    leads: GeneratedLead[];
  }> => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/lead-generation/enrichment-progress',
      { params: { leadIds: leadIds.join(',') } }
    );
    return response.data.data;
  },

  // ===== UNIFIED DISCOVERY + ENRICHMENT PIPELINE =====

  /**
   * Single call: finds companies + contacts together (~40-50s)
   * Replaces the old 2-step flow (fast-discovery → batch-enrich)
   */
  discoverAndEnrich: async (params: {
    industry: string;
    location: string;
    targetRole?: string;
    optionalContext?: string;
    count?: number;
  }): Promise<{
    leads: GeneratedLead[];
    totalCompanies: number;
    totalContacts: number;
    duration: number;
  }> => {
    const response = await apiClient.post<ApiResponse<any>>(
      '/lead-generation/discover-and-enrich',
      params,
      { timeout: 180000 } // 3 minutes
    );
    return response.data.data;
  },
};
